import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { memo, useMemo } from 'react';
import { DateRange } from 'react-aria-components';
import { Line } from 'react-chartjs-2';

import { useTimezone } from '@/hooks';
import { formatDateShort } from '@/utils';

import { useStatistics } from './useStatistics';
import { getData, MetricType } from './utils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

export type GlobalGraphProps = {
  dateRange: DateRange | null;
  dateSelection: number | string;
  selectedMetric: MetricType;
};

const IGlobalGraph = ({ dateRange, dateSelection, selectedMetric }: GlobalGraphProps) => {
  const timezone = useTimezone();

  const { statistics } = useStatistics({ dateRange, dateSelection });

  const { labels, runsPerDay, automationPerDay, fullAutomationPerDay, flaggedPerDay, editsPerDay } = useMemo(() => {
    if (!dateRange) return {};

    const perDay = statistics?.statistics.globalPerDay;
    const labels = [];
    const runsPerDay = [];
    const automationPerDay = [];
    const fullAutomationPerDay = [];
    const flaggedPerDay = [];
    const editsPerDay = [];

    const start = dateRange.start.toDate(timezone);
    const end = dateRange.end.toDate(timezone);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const isoDate = d.toISOString().slice(0, 10);
      const data = getData(perDay?.[isoDate]);

      labels.push(formatDateShort(d));
      runsPerDay.push(data.numDocuments);
      automationPerDay.push(data.fractionValuesAutomated);
      fullAutomationPerDay.push(data.fractionDocumentsAutomated);
      flaggedPerDay.push(data.fractionValuesFlagged);
      editsPerDay.push(data.fractionValuesEdited);
    }

    return {
      labels,
      runsPerDay,
      automationPerDay,
      fullAutomationPerDay,
      flaggedPerDay,
      editsPerDay,
    };
  }, [dateRange, statistics, timezone]);

  const selectedData = useMemo(() => {
    let data;

    switch (selectedMetric) {
      case 'runs':
        data = runsPerDay;
        break;
      case 'automation':
        data = automationPerDay;
        break;
      case 'fullAutomation':
        data = fullAutomationPerDay;
        break;
      case 'flagged':
        data = flaggedPerDay;
        break;
      case 'edits':
        data = editsPerDay;
        break;
      default:
        data = runsPerDay;
    }

    return data;
  }, [automationPerDay, editsPerDay, flaggedPerDay, fullAutomationPerDay, runsPerDay, selectedMetric]);

  const globalOptions = useMemo(() => {
    let ticks;
    if (selectedMetric === 'runs') {
      ticks = {
        callback: (value: number) => {
          return value.toFixed(0).toString();
        },
        stepSize: 1,
        min: 0,
      };
    } else {
      ticks = {
        callback: (value: number) => {
          return `${(value * 100).toFixed(0)}%`;
        },
        stepSize: 0.1,
        min: 0,
        max: 1,
      };
    }

    return {
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            autoSkip: true,
            maxTicksLimit: 3,
          },
        },
        y: { ticks },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      elements: {
        point: {
          radius: 0,
          hoverRadius: 3,
        },
      },
    };
  }, [selectedMetric]);

  const globalData = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          data: selectedData,
          borderColor: '#84CAFF',
          borderWidth: 2,
          backgroundColor: '#84CAFF',
          fill: true,
          tension: 0.2,
          pointRadius: 0,
          pointBackgroundColor: '#84CAFF',
        },
      ],
    };
  }, [labels, selectedData]);

  return <Line options={globalOptions} data={globalData} />;
};

export const GlobalGraph = memo(IGlobalGraph) as typeof IGlobalGraph;
